import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { CircularProgress } from "@/components/ui/circular-progress"
import { useLessonsMessages } from "@/modules/activities/lessons/stores/use-lessons-message"
import { MessageSquareText, X } from "lucide-react"
import { useNavigate } from "react-router-dom"
import useInvalidateStudyPlannerQueries from "@/modules/activities/hooks/use-invalidate-queries"
import { useEffect } from "react"
import mixpanelClient from "@/services/mixpanel"
import { useAuth } from "@/modules/auth/store"
import { useCosmos } from "@/modules/cosmos/stores/use-cosmos-store"
import { PRODUCT_TOUR_V2_STEPS } from "@/modules/product-tour-v2/types/steps"
import { useProductTourV2 } from "@/modules/product-tour-v2/stores/use-product-tour"

export default function LessonsSummaryPage() {
  const navigate = useNavigate()
  const { indications } = useLessonsMessages()
  const handleInvalidateQueries = useInvalidateStudyPlannerQueries()
  const { user } = useAuth()
  const { cosmosKey } = useCosmos()
  const { setCurrentStep } = useProductTourV2()

  const handleContinueStudying = () => {
    handleInvalidateQueries()
    if (user.productTour === null) {
      setCurrentStep(PRODUCT_TOUR_V2_STEPS.MY_SITUATION)
      navigate("/my-situation")
      return
    }
    navigate("/study", { replace: true })
  }

  const handleCloseButtonClick = () => {
    handleInvalidateQueries()
    if (user.productTour === null) {
      setCurrentStep(PRODUCT_TOUR_V2_STEPS.MY_SITUATION)
      navigate("/my-situation")
      return
    }
    navigate("/study", { replace: true })
  }

  useEffect(() => {
    mixpanelClient.activities.end({
      name: `${user.firstName} ${user.lastName}`,
      email: user.emailAddress,
      approved: true,
      endDate: new Date().toISOString(),
      score: 100,
      organization: cosmosKey,
    })
  }, [])

  return (
    <section className="z-5 absolute left-0 top-0 flex min-h-screen w-full flex-col gap-4 bg-gradient-to-b from-primary-50 to-neutral-50 px-2 py-6 lg:flex-row lg:gap-8">
      <section className="flex h-fit w-full flex-col gap-8">
        {/* Header */}
        <div className="flex w-full items-start justify-between">
          <div className="flex-1"></div>
          <div className="flex flex-1 flex-col items-center gap-4">
            <h1 className="text-center text-lg font-bold">Resumen</h1>
            <div className="flex items-center gap-2 rounded-xl border-[3px] border-primary-400 px-4 py-2">
              <MessageSquareText className="text-primary-500" />
              <span className="font-medium text-primary-500">Lecciones</span>
            </div>
          </div>
          <div className="flex flex-1 justify-end">
            <Button variant="ghost" size="icon" onClick={handleCloseButtonClick}>
              <X className="!size-6 text-neutral-600" />
            </Button>
          </div>
        </div>
        {/* Content */}
        <section className="flex w-full flex-col gap-10">
          <article className="mx-auto flex max-w-6xl flex-col gap-10">
            <div className="flex flex-col items-center gap-2">
              <CircularProgress strokeWidth={30} size="xl" progress={100} />
            </div>
            <div>
              <h2 className="text-center text-3xl font-bold text-primary-500">
                ¡Lección cumplida! ✅
              </h2>
              <p className="text-center text-sm font-semibold text-muted-foreground">
                Finalizaste esta actividad por completo. ¡Seguí avanzando hacia tus objetivos!
              </p>
            </div>
            <div className="space-y-4">
              <h4 className="text-left text-sm font-semibold text-primary-500">
                ¿Que podés mejorar?
              </h4>
              {indications.map((indicator) => (
                <div className="flex items-start gap-2" key={indicator}>
                  <Checkbox checked className="size-4" />
                  <p className="w-[65ch] text-sm font-medium leading-[143%]">{indicator}</p>
                </div>
              ))}
            </div>
            <Button onClick={handleContinueStudying} className="w-full">
              Continuar estudiando
            </Button>
          </article>
        </section>
      </section>
    </section>
  )
}
